import {ExclamationCircleIcon} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import {useMemo} from 'react';

import Col from '../../common/Col';
import Row from '../../common/Row';

export type MessageBoxProps = {
  id?: string;
  className?: string;
  top?: React.ReactNode;
  topLeft?: React.ReactNode;
  midLeft?: React.ReactNode;
  topRight?: React.ReactNode;
  midRight?: React.ReactNode;
  bottom?: React.ReactNode;
  // If true, the bottom element overlaps the content by a fixed amount. Otherwise, it overlaps by half its height.
  bottomIsTall?: boolean;
  bottomButton?: React.ReactNode;
  // If true, the bottom button is not visible but space for it is reserved
  bottomButtonInvisible?: boolean;
  alertText?: string;
  onClickBottomButton?: () => void;
  onMouseDownBottomButton?: () => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  dark?: boolean;
  contentClassName?: string;
};

const MessageBox = (props: MessageBoxProps) => {
  const {
    id,
    top,
    topLeft,
    midLeft,
    topRight,
    midRight,
    bottom,
    bottomButton,
    bottomIsTall,
    bottomButtonInvisible = false,
    alertText,
    dark,
    contentClassName,
  } = props;
  const hasLeftElements = useMemo(() => !!topLeft || !!midLeft, [midLeft, topLeft]);
  const hasRightElements = useMemo(() => !!topRight || !!midRight, [midRight, topRight]);
  const hasTopElements = useMemo(() => !!topLeft || !!topRight, [topLeft, topRight]);
  const hasMidElements = useMemo(() => !!midLeft || !!midRight, [midLeft, midRight]);
  return (
    <Col id={id} className={classNames('relative', top && 'mt-16')} style={props.style}>
      {top && <div className="absolute -top-16 w-full">{top}</div>}
      {topLeft && <div className="absolute top-10">{topLeft}</div>}
      {midLeft && <div className="absolute top-60">{midLeft}</div>}
      {topRight && <div className="absolute top-10 right-0">{topRight}</div>}
      {midRight && <div className="absolute top-60 right-0">{midRight}</div>}
      <Col
        className={classNames(
          'grow border-normal font-sans rounded-[3rem] min-w-[3rem] border-gray-100 shadow-solid-light-green justify-center items-center overflow-hidden',
          dark ? 'bg-primary-700 text-white' : 'bg-white text-primary-700',
          top && 'pt-16',
          (hasLeftElements || hasRightElements) && 'mx-12 px-12',
          hasTopElements && !hasMidElements && 'min-h-[10.5rem]',
          hasMidElements && 'min-h-[24rem]',
          bottom && 'pb-xl mobile:pb-lg',
          bottomButton && 'pb-0',
          alertText && '!border-red-200 !bg-red-100',
          props.className
        )}
      >
        <div className={classNames('p-xs grow w-full', contentClassName)}>{props.children}</div>
        {bottomButton && (
          <button
            className={classNames(
              'w-full rounded-b-[3rem] bg-primary-700 text-white cursor-pointer py-2 px-4 hover:bg-primary-600',
              bottomButtonInvisible && 'invisible',
              'focus-visible:-outline-offset-4'
            )}
            onClick={props.onClickBottomButton}
            onMouseDown={props.onMouseDownBottomButton}
          >
            {bottomButton}
          </button>
        )}
        {alertText && (
          <Row className="bg-red-200 w-full text-white font-semibold justify-center px-4">
            {alertText}
            <ExclamationCircleIcon className="w-sm ml-4" />
          </Row>
        )}
      </Col>
      {bottom && (
        <div className={classNames('w-full', bottomIsTall ? '-translate-y-lg' : '-translate-y-1/2')}>{bottom}</div>
      )}
    </Col>
  );
};

export default MessageBox;
